/**
 * "No, only at set times": private-showing times picked day by day on a month calendar. A day's time is
 * an availability exception of kind `window`, which the booking engine honours even on a weekday with
 * no weekly rule (it replaces that day's rule-derived windows). The exception model holds one window
 * per date, so a day has one time; picking another time for it changes it.
 */

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import {
  createAvailabilityException,
  deleteAvailabilityException,
  updateAvailabilityException,
  type AvailabilityException,
} from '../../../../lib/leasing.api';
import { cn } from '../../../../lib/utils';
import { LifecycleConfirmDialog } from '../shared';
import {
  DANGER_LINK_BUTTON_CLASS,
  InlineError,
  LINK_BUTTON_CLASS,
  ShowingsCard,
  usePanelReport,
  type PanelReport,
} from './panel-support';
import { TimeSelect } from './TimeSelect';
import {
  addMonths,
  exceptionDateKey,
  formatDateKey,
  formatMonthLabel,
  formatShortDay,
  formatTimeRange,
  monthGrid,
  monthStartKey,
  timeToMinutes,
} from './time-utils';
import { useAvailabilityAction } from './useAvailabilityAction';

const QUICK_WINDOWS = [
  { key: 'morning', label: 'Morning 9–12', startTime: '09:00', endTime: '12:00' },
  { key: 'afternoon', label: 'Afternoon 1–5', startTime: '13:00', endTime: '17:00' },
  { key: 'evening', label: 'Evening 5–8', startTime: '17:00', endTime: '20:00' },
] as const;

const WEEKDAY_HEADINGS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface SpecificTimesPanelProps {
  listingId: string;
  isClosed: boolean;
  exceptions: AvailabilityException[];
  /** `YYYY-MM-DD` today in the listing's timezone. */
  today: string;
  report: PanelReport;
}

export function SpecificTimesPanel({ listingId, isClosed, exceptions, today, report }: SpecificTimesPanelProps) {
  const { run, saving, error, clearError } = useAvailabilityAction(listingId);
  const [monthKey, setMonthKey] = useState(() => monthStartKey(today));
  const [selected, setSelected] = useState<string | null>(null);
  const [startTime, setStartTime] = useState('13:00');
  const [endTime, setEndTime] = useState('17:00');
  const [touched, setTouched] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [copyTargets, setCopyTargets] = useState<Set<string> | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  // A picked day with an unsaved time is an open form: leaving the step should ask first.
  usePanelReport(report, saving, (selected !== null && touched) || copyTargets !== null);

  const windowByDate = new Map<string, AvailabilityException>();
  const blockedDates = new Set<string>();
  for (const ex of exceptions) {
    const key = exceptionDateKey(ex.date);
    if (ex.kind === 'window') windowByDate.set(key, ex);
    else blockedDates.add(key);
  }
  const upcoming = [...windowByDate.entries()].filter(([date]) => date >= today).sort(([a], [b]) => a.localeCompare(b));
  const selectedWindow = selected ? windowByDate.get(selected) : undefined;

  const selectDay = (date: string) => {
    clearError();
    setFormError(null);
    setCopyTargets(null);
    setSelected(date);
    const existing = windowByDate.get(date);
    setStartTime(existing?.startTime ?? '13:00');
    setEndTime(existing?.endTime ?? '17:00');
    setTouched(false);
  };

  const problemFor = (date: string): string | null => {
    if (date < today) return 'Choose today or a later date.';
    if (blockedDates.has(date)) return `${formatShortDay(date)} is blocked. Remove the block first.`;
    return null;
  };

  const writeDay = async (date: string, start: string, end: string) => {
    const existing = windowByDate.get(date);
    if (existing) await updateAvailabilityException(listingId, existing.id, { startTime: start, endTime: end });
    else await createAvailabilityException(listingId, { date, kind: 'window', startTime: start, endTime: end, notes: null });
  };

  const saveDay = async () => {
    if (!selected) return;
    const problem =
      problemFor(selected) ??
      (timeToMinutes(endTime) <= timeToMinutes(startTime) ? 'The end time must be after the start time.' : null);
    if (problem) {
      setFormError(problem);
      return;
    }
    setFormError(null);
    const ok = await run(() => writeDay(selected, startTime, endTime), 'Failed to save the time');
    if (ok) setTouched(false);
  };

  const copyToTargets = async () => {
    if (!selectedWindow || !copyTargets || copyTargets.size === 0) return;
    const ok = await run(async () => {
      for (const date of [...copyTargets].sort()) {
        await writeDay(date, selectedWindow.startTime!, selectedWindow.endTime!);
      }
    }, 'Failed to copy the time');
    if (ok) setCopyTargets(null);
  };

  const removeDay = async () => {
    if (!confirmRemove) return;
    const existing = windowByDate.get(confirmRemove);
    if (!existing) {
      setConfirmRemove(null);
      return;
    }
    const ok = await run(() => deleteAvailabilityException(listingId, existing.id), 'Failed to remove the time');
    if (ok) {
      if (selected === confirmRemove) setTouched(false);
      setConfirmRemove(null);
    }
  };

  const pendingRow = selected && touched && !selectedWindow;
  const rows: { date: string; start: string; end: string; pending: boolean }[] = upcoming.map(([date, ex]) => ({
    date,
    start: ex.startTime!,
    end: ex.endTime!,
    pending: false,
  }));
  if (pendingRow) rows.push({ date: selected, start: startTime, end: endTime, pending: true });
  rows.sort((a, b) => a.date.localeCompare(b.date));

  return (
    <>
      <ShowingsCard
        data-testid="specific-times-panel"
        title="Private showings"
        description="Choose a day, then the hours you are free. Prospects can request a time inside that window."
      >
        <div className="grid gap-6 md:grid-cols-[minmax(0,20rem)_1fr]">
          <div data-testid="specific-times-calendar">
            <div className="mb-2 flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="min-h-[44px] min-w-[44px]"
                aria-label="Previous month"
                data-testid="specific-times-month-prev"
                disabled={monthKey <= monthStartKey(today)}
                onClick={() => setMonthKey(addMonths(monthKey, -1))}
              >
                <ChevronLeft aria-hidden="true" />
              </Button>
              <span className="text-sm font-semibold text-foreground" data-testid="specific-times-month-label" aria-live="polite">
                {formatMonthLabel(monthKey)}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="min-h-[44px] min-w-[44px]"
                aria-label="Next month"
                data-testid="specific-times-month-next"
                onClick={() => setMonthKey(addMonths(monthKey, 1))}
              >
                <ChevronRight aria-hidden="true" />
              </Button>
            </div>
            <div className="grid grid-cols-7 text-center text-xs font-medium text-muted-foreground">
              {WEEKDAY_HEADINGS.map((d) => (
                <div key={d} className="py-1">
                  {d}
                </div>
              ))}
            </div>
            {monthGrid(monthKey).map((week, wi) => (
              <div key={wi} className="grid grid-cols-7">
                {week.map((date, di) => {
                  if (!date) return <div key={di} />;
                  const isPast = date < today;
                  const hasTimes = windowByDate.has(date);
                  const isSelected = date === selected;
                  const isBlocked = blockedDates.has(date);
                  return (
                    <button
                      key={date}
                      type="button"
                      data-testid={`specific-day-${date}`}
                      disabled={isClosed || isPast}
                      aria-pressed={isSelected}
                      aria-label={`${formatDateKey(date, { month: 'long', day: 'numeric' })}${hasTimes ? ', has times' : ''}${isBlocked ? ', blocked' : ''}`}
                      onClick={() => selectDay(date)}
                      className={cn(
                        'm-0.5 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-sm transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        isSelected
                          ? 'bg-primary font-semibold text-primary-foreground'
                          : hasTimes
                            ? 'bg-primary/15 font-semibold text-primary'
                            : 'text-foreground hover:bg-muted',
                        isBlocked && !isSelected && 'line-through',
                        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent'
                      )}
                    >
                      {Number(date.slice(8))}
                    </button>
                  );
                })}
              </div>
            ))}
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="primary">Has times</Badge> Days with times already set
            </div>
          </div>

          <div data-testid="specific-day-panel">
            {!selected ? (
              <p className="text-sm text-muted-foreground">Choose a day on the calendar to set its time.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <h4 className="text-base font-semibold text-foreground">
                    {formatDateKey(selected, { weekday: 'long', month: 'long', day: 'numeric' })}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {selectedWindow ? 'This day has a time. Pick another to change it.' : 'Add the time you can show it on this day.'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Quick times">
                  {QUICK_WINDOWS.map((quick) => {
                    const active = startTime === quick.startTime && endTime === quick.endTime;
                    return (
                      <button
                        key={quick.key}
                        type="button"
                        data-testid={`specific-quick-${quick.key}`}
                        aria-pressed={active}
                        disabled={saving}
                        onClick={() => {
                          setStartTime(quick.startTime);
                          setEndTime(quick.endTime);
                          setTouched(true);
                          setFormError(null);
                        }}
                        className={cn(
                          'min-h-[44px] rounded-full border px-4 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          active
                            ? 'border-primary bg-primary/15 text-primary'
                            : 'border-border bg-background text-foreground hover:bg-muted'
                        )}
                      >
                        {quick.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-3">
                  <TimeSelect
                    id="specific-start"
                    label="From"
                    value={startTime}
                    disabled={saving}
                    data-testid="specific-start"
                    onChange={(value) => {
                      setStartTime(value);
                      setTouched(true);
                    }}
                  />
                  <TimeSelect
                    id="specific-end"
                    label="To"
                    value={endTime}
                    disabled={saving}
                    data-testid="specific-end"
                    onChange={(value) => {
                      setEndTime(value);
                      setTouched(true);
                    }}
                  />
                </div>
                {(formError ?? (error && !confirmRemove ? error : null)) && (
                  <InlineError message={(formError ?? error)!} testId="specific-times-error" />
                )}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" data-testid="specific-add-day" disabled={saving} onClick={() => void saveDay()}>
                    {saving ? 'Saving…' : selectedWindow ? 'Change this day' : 'Add to this day'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="specific-copy-days"
                    disabled={saving || !selectedWindow}
                    onClick={() => setCopyTargets(new Set())}
                  >
                    Copy to other days…
                  </Button>
                </div>

                {copyTargets && selectedWindow && (
                  <div data-testid="specific-copy-panel" className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
                    <p className="text-sm font-medium text-foreground">
                      Copy {formatTimeRange(selectedWindow.startTime!, selectedWindow.endTime!)} to
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {monthGrid(monthKey)
                        .flat()
                        .filter((d): d is string => !!d && d >= today && d !== selected && !blockedDates.has(d))
                        .map((date) => {
                          const on = copyTargets.has(date);
                          return (
                            <button
                              key={date}
                              type="button"
                              data-testid={`specific-copy-target-${date}`}
                              aria-pressed={on}
                              onClick={() => {
                                const next = new Set(copyTargets);
                                if (on) next.delete(date);
                                else next.add(date);
                                setCopyTargets(next);
                              }}
                              className={cn(
                                'min-h-[44px] min-w-[44px] rounded-md border px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                on ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-muted'
                              )}
                            >
                              {formatDateKey(date, { weekday: 'short', day: 'numeric' })}
                            </button>
                          );
                        })}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        data-testid="specific-copy-confirm"
                        disabled={saving || copyTargets.size === 0}
                        onClick={() => void copyToTargets()}
                      >
                        {saving ? 'Copying…' : 'Copy'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="specific-copy-cancel"
                        disabled={saving}
                        onClick={() => setCopyTargets(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </ShowingsCard>

      <ShowingsCard
        data-testid="specific-times-list"
        title="Your times"
        titleAdornment={
          <span className="text-sm text-muted-foreground">
            {upcoming.length} {upcoming.length === 1 ? 'day' : 'days'}
          </span>
        }
      >
        {rows.length === 0 ? (
          <p data-testid="specific-times-empty" className="text-sm text-muted-foreground">
            None added.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((row) => (
              <li
                key={row.date}
                data-testid={`specific-time-row-${row.date}`}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2"
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold text-foreground">{formatShortDay(row.date)}</span>
                  <span className="text-muted-foreground">{formatTimeRange(row.start, row.end)}</span>
                  {row.pending && <Badge variant="warning">Not added yet</Badge>}
                </div>
                {!isClosed && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      data-testid={`specific-time-edit-${row.date}`}
                      disabled={saving}
                      aria-label={`Edit ${formatShortDay(row.date)}`}
                      onClick={() => {
                        setMonthKey(monthStartKey(row.date));
                        selectDay(row.date);
                      }}
                      className={LINK_BUTTON_CLASS}
                    >
                      Edit
                    </button>
                    {!row.pending && (
                      <button
                        type="button"
                        data-testid={`specific-time-remove-${row.date}`}
                        disabled={saving}
                        aria-label={`Remove ${formatShortDay(row.date)}`}
                        onClick={() => {
                          clearError();
                          setConfirmRemove(row.date);
                        }}
                        className={DANGER_LINK_BUTTON_CLASS}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </ShowingsCard>

      <LifecycleConfirmDialog
        open={confirmRemove !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmRemove(null);
        }}
        title="Remove this time?"
        description={
          confirmRemove ? `Prospects will no longer be able to request a showing on ${formatShortDay(confirmRemove)}.` : ''
        }
        confirmLabel="Remove time"
        pendingLabel="Removing…"
        onConfirm={() => void removeDay()}
        isSubmitting={saving}
        destructive
        testIdPrefix="specific-time-remove-confirm"
      >
        {error && confirmRemove && <p className="mb-3 text-sm text-destructive" role="alert">{error}</p>}
      </LifecycleConfirmDialog>
    </>
  );
}
